import { useEffect, useRef, useState } from 'react';
import { GameState } from '../../types/game';
import { loadGameState, saveGameState } from '../../utils/gamePersistence';
import { getSessionPlayerId } from '../../utils/sessionPlayer';
import { loadHostFlag } from '../../utils/hostFlag';
import { saveHostFlag } from '../../utils/hostFlag';
import { GameAction } from '../gameReducer';

interface UseLocalPersistenceParams {
  gameState: GameState;
  dispatch: React.Dispatch<GameAction>;
  isHost: boolean;
  setIsHost: (v: boolean) => void;
  setCurrentClientPlayerId: (id: string | null) => void;
  modeHint?: GameState['mode'];
}

interface UseLocalPersistenceResult {
  hydrated: boolean;
}

/**
 * Phase 6: Extract local persistence (hydration + autosave debounce + passive polling for non-host clients)
 * Responsibilities:
 *  - On mount: load local snapshot unless we are already an ONLINE hydrated game (mode ONLINE with lobby & id)
 *  - Autosave of any gameState change (deduped by serialized snapshot)
 *  - Non-host passive polling every 2s (if same game id) to adopt remote-local changes produced by host (local storage sync)
 */
// host flag helper now imported from utils/hostFlag

export function useLocalPersistence({
  gameState,
  dispatch,
  isHost,
  setIsHost,
  setCurrentClientPlayerId,
  modeHint,
}: UseLocalPersistenceParams): UseLocalPersistenceResult {
  const [hydrated, setHydrated] = useState(false);
  const lastSerializedRef = useRef<string | null>(null);
  const currentMode = (gameState.mode || 'REAL') as string;
  const modeHintValue = modeHint as GameState['mode'] | undefined;
  const hasOnlineHint = modeHintValue === ('ONLINE' as GameState['mode']);

  // Hydration
  useEffect(() => {
    if (hydrated) return; // idempotent
    const markHydrated = () => setHydrated(true);
    if (currentMode === 'ONLINE' || hasOnlineHint) {
      markHydrated();
      return;
    }
    const saved = loadGameState();
    if (saved) {
      dispatch({ type: 'LOAD_GAME', gameState: saved });
      const persistedHost = loadHostFlag();
      const inferredHost = saved.mode === 'REAL';
      const nextHost = inferredHost ? true : (hasOnlineHint ? false : persistedHost);
      setIsHost(nextHost);
      if (nextHost) {
        saveHostFlag(true);
      }
      if (saved.mode === 'ONLINE') {
        const sp = getSessionPlayerId();
        if (sp && saved.players.some(p => p.id === sp)) {
          setCurrentClientPlayerId(sp);
        }
      } else {
        setCurrentClientPlayerId(null);
      }
    } else {
      setCurrentClientPlayerId(null);
    }
    markHydrated();
  }, [currentMode, dispatch, gameState.players, hasOnlineHint, hydrated, setCurrentClientPlayerId, setIsHost]);

  // Autosave (serialize first to avoid unnecessary writes)
  useEffect(() => {
    if (!hydrated) return;
    if (currentMode === 'ONLINE') return;
    const hasGame = Boolean(gameState.id) || gameState.players.length > 0 || gameState.history.length > 0 || gameState.startedAt;
    if (!hasGame) return;
    try {
      const serialized = JSON.stringify(gameState);
      if (serialized === lastSerializedRef.current) return;
      saveGameState(gameState);
      lastSerializedRef.current = serialized;
    } catch {}
  }, [currentMode, gameState, hydrated]);

  // Passive polling for non-host
  useEffect(() => {
    if (!hydrated) return;
    if (currentMode === 'ONLINE') return;
    if (isHost) return;
    const interval = setInterval(() => {
      try {
        const saved = loadGameState();
        if (saved && saved.id === gameState.id) {
          const currentSerialized = lastSerializedRef.current || JSON.stringify(gameState);
          const savedSerialized = JSON.stringify(saved);
          if (savedSerialized !== currentSerialized) {
            dispatch({ type: 'LOAD_GAME', gameState: saved });
            lastSerializedRef.current = savedSerialized;
          }
        }
      } catch {}
    }, 2000);
    return () => clearInterval(interval);
  }, [currentMode, gameState.id, isHost, hydrated, dispatch, gameState]);

  return { hydrated };
}
